/**
 * LexisNexis identity check result — the content Risk Management already uses, restyled to
 * the officer app's look, plus how the FraudPoint score becomes the in-app outcome (the
 * gate's own preview, the saved cut-offs, the enforcement setting, the officer screens' labels).
 *
 * Scales shown here are cited, not invented (docs/LexisNexis API Information):
 *   FraudPoint 300 (highest risk) - 999 (lowest); risk indices 1 (lowest) - 9 (highest) — BIS354
 *   DOB match level 0-8, 8 = full month/day/year match — TIER1-IMPLEMENTATION-SYNTHESIS.md
 *   NAP / NAS codes — PCB's InstantID policy export (a1r9yfwb_policy_instantid_2231691.json)
 *   Emailage score 0 (lowest risk) - 999, bands 1-6 — BIS367 p.8
 */

import { useMemo } from 'react';

import type { LnTestResponse, Tone } from '@/services/verificationTestApi';

import { Banner, Panel, Pill, RawResponse, RuleSteps, SectionTitle, SettingsTable } from './consoleUi';

export const NAP_LABEL: Record<number, string> = {
  0: 'Nothing found', 1: 'Phone found with a different name and address', 2: 'First and last name matched',
  3: 'First name and address matched', 4: 'First name and phone matched', 5: 'Last name and address matched',
  6: 'Address and phone matched', 7: 'Last name and phone matched', 8: 'First name, last name and address matched',
  9: 'First name, last name and phone matched', 10: 'First name, address and phone matched',
  11: 'Last name, address and phone matched', 12: 'First name, last name, address and phone all matched',
};
export const NAS_LABEL: Record<number, string> = {
  0: 'Nothing matched', 1: 'SSN found with a different name and address', 2: 'First and last name matched',
  3: 'First name and address matched', 4: 'First name and SSN matched', 5: 'Last name and address matched',
  6: 'Address and SSN matched', 7: 'Last name and SSN matched', 8: 'First name, last name and address matched',
  9: 'First name, last name and SSN matched', 10: 'First name, address and SSN matched',
  11: 'Last name, address and SSN matched', 12: 'First name, last name, address and SSN all matched',
};

export const INDEX_LABEL: Record<string, string> = {
  synthetic: 'Synthetic identity (made-up identity)',
  stolen: 'Stolen identity',
  manipulated: 'Manipulated identity (altered or mis-keyed details)',
  vulnerableVictim: 'Vulnerable victim (minor or elderly identity at risk)',
  friendlyFraud: 'Friendly fraud (someone close to the person)',
  suspiciousActivity: 'Suspicious activity history',
};

/**
 * Plain-language reading of common LexisNexis indicators. LexisNexis's own wording is shown
 * first and is authoritative; this is what it usually means for a banker.
 */
const INDICATOR_NOTES: Array<[RegExp, string]> = [
  [/associated with multiple last names/i,
    'LexisNexis has records tying this SSN to more than one surname. Common for married or divorced customers and for records with typos; it weighs more when the Synthetic or Stolen index is also raised.'],
  [/not linked in reporting sources|not verified/i,
    'LexisNexis’s sources do not show these details together for this person — often a new or mobile phone, or a recent move. See the Name / Address / Phone line below for exactly what matched.'],
  [/velocity of recent search activity/i,
    'This identity has been searched many times recently across LexisNexis customers — including repeated test runs here. Each run adds to it and lowers the FraudPoint score for a while.'],
  [/velocity of identity events/i,
    'An unusually high number of recent events tied to this identity in LexisNexis’s sources (for example applications or record changes). Repeated searches can contribute.'],
  [/mobile number/i, 'The phone given is a mobile number. Information only.'],
];

export function codeOf(v: string | null | undefined): number | null {
  const m = (v ?? '').match(/\d+/);
  return m ? Number.parseInt(m[0], 10) : null;
}

export function bandTone(band: string | null | undefined): Tone {
  return band === 'Low' ? 'ok' : band === 'Moderate' ? 'warn' : band === 'High' ? 'err' : 'muted';
}

export function parseJson<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

export function alertsOf(r: LnTestResponse): Array<{ text: string; source: string; code: string }> {
  const codes = parseJson<{ items?: Array<{ provider: string; code: string; text: string }> }>(r.riskCodesJson, {}).items ?? [];
  const indicators = parseJson<string[]>(r.potentialRiskIndicatorsJson, []);
  return [
    ...codes.map((c) => ({ text: c.text, source: c.provider, code: c.code })),
    ...indicators.filter((t) => !codes.some((c) => c.text === t)).map((t) => ({ text: t, source: '', code: '' })),
  ];
}

export function LnResultPanel({ r }: { r: LnTestResponse }): JSX.Element {
  const all = useMemo(() => alertsOf(r), [r]);

  if (!r.success) {
    return (
      <Panel title="LexisNexis identity check" tone="err">
        <Banner tone="err" title="No result">
          {r.error} {r.errorCode ? <span className="vc-mono">({r.errorCode})</span> : null}
          {r.note && <div>{r.note}</div>}
        </Banner>
      </Panel>
    );
  }

  const fp = r.fraudPoint!;
  const iid = r.instantId!;
  const t = r.transparency;
  const v = t?.verdict;
  const nap = codeOf(iid.nameAddressPhone);
  const nas = codeOf(iid.nameAddressSsn);
  const dob = codeOf(iid.dobMatchLevel);
  const s = r.subject;

  return (
    <Panel
      title="LexisNexis identity check"
      tone={bandTone(fp.band)}
      actions={
        <>
          {r.mock ? <Pill tone="warn">Test fixture — not live</Pill> : <Pill tone="info">Live LexisNexis</Pill>}
          {t?.billed && !r.mock && <Pill tone="warn">Billed call</Pill>}
          <Pill tone="muted">{r.environment === 'sandbox' ? 'LexisNexis test environment' : 'Production'}</Pill>
        </>
      }
    >
      {s && (
        <p className="vc-hint">
          Checked: {[s.firstName, s.middleInitial, s.lastName].filter(Boolean).join(' ')} · DOB {s.dob} · SSN …{s.taxIdLast4 ?? '—'} ·{' '}
          {[s.street, s.city, s.state, s.zip].filter(Boolean).join(', ')} · {s.phone || 'no phone'} · {s.email || 'no e-mail'}
          {r.subjectSource === 'core-director' ? ' (from Core Director)' : r.subjectSource === 'core-director-edited' ? ` (from Core Director, changed: ${(r.editedFields ?? []).join(', ')})` : ''}
        </p>
      )}

      <div className="vc-verdict">
        <span className="vc-verdict__score">{fp.score ?? '—'}</span>
        <Pill tone={bandTone(fp.band)}>{fp.band ? `${fp.band} risk` : 'No score'}</Pill>
        {v && <span className="vc-verdict__text">{v.plain}</span>}
      </div>
      {v && (
        <Banner tone={t!.gate.wouldBlock ? 'err' : t!.gate.requirementUnmet ? 'warn' : 'ok'} title="Effect on account opening">
          {v.commitText}
          <div className="vc-muted" style={{ marginTop: 2 }}>Graded as a new {t!.gate.customerType} applicant. {t!.gate.assumes}</div>
        </Banner>
      )}

      {v && (
        <div className="vc-two">
          <div>
            <SectionTitle>How the score was graded</SectionTitle>
            <RuleSteps steps={v.rules} />
          </div>
          <div>
            <SectionTitle>What the officer app shows for this score</SectionTitle>
            <dl className="vc-kv">
              <dt>Review step chip</dt><dd>{v.officerReviewChip}</dd>
              <dt>Person dialog guidance</dt><dd>{v.officerGuidanceLabel}</dd>
              <dt>Identity report</dt><dd>{v.officerReportLabel}</dd>
            </dl>
            {v.fixedScreensNote && (
              <div style={{ marginTop: 8 }}>
                <Banner tone={v.fixedScreensDiffer ? 'warn' : 'info'}>{v.fixedScreensNote}</Banner>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="vc-two">
        <div>
          <SectionTitle>What drove the score — FraudPoint risk indices</SectionTitle>
          <p className="vc-hint">Each runs 1 (no warning) to 9 (strongest). Anything above 1 is a contributing concern.</p>
          <table className="vc-table">
            <tbody>
              {Object.entries(fp.indices).map(([k, val]) => (
                <tr key={k}>
                  <th scope="row">{INDEX_LABEL[k] ?? k}</th>
                  <td><Pill tone={val == null ? 'muted' : val >= 5 ? 'err' : val > 1 ? 'warn' : 'ok'}>{val ?? '—'} / 9</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <SectionTitle>Identity match (InstantID)</SectionTitle>
          <table className="vc-table">
            <tbody>
              <tr><th scope="row">Name / address / phone</th><td>{nap ?? '—'} — {nap != null ? NAP_LABEL[nap] ?? '' : 'not returned'}</td></tr>
              <tr><th scope="row">Name / address / SSN</th><td>{nas ?? '—'} — {nas != null ? NAS_LABEL[nas] ?? '' : 'not returned'}</td></tr>
              <tr><th scope="row">Date of birth</th><td>{dob ?? '—'} / 8 {dob === 8 ? '— full month, day and year match' : dob != null ? '— partial match' : ''}</td></tr>
              <tr><th scope="row">Verification Risk Index</th><td>{iid.verificationRiskIndex ?? '—'} / 50 <span className="vc-muted">(0 highest risk, 50 lowest; context only — does not gate)</span></td></tr>
              <tr>
                <th scope="row">E-mail (Emailage)</th>
                <td>
                  {!r.emailage?.called ? 'Not checked (no e-mail given, or not part of this run).'
                    : <>Score {r.emailage.score ?? '—'} <span className="vc-muted">(0 lowest risk – 999 highest; context only)</span> {r.emailage.advice}</>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <SectionTitle>Alerts LexisNexis raised</SectionTitle>
        {all.length === 0 ? <p className="vc-hint">None.</p> : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {all.map((a, i) => {
              const note = INDICATOR_NOTES.find(([re]) => re.test(a.text))?.[1];
              return (
                <li key={i} style={{ marginBottom: 6 }}>
                  <strong>{a.text}</strong>
                  {a.source && <span className="vc-muted"> — {a.source}{a.code ? ` code ${a.code}` : ''}</span>}
                  {note && <div className="vc-hint">What this usually means: {note}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <SectionTitle>What ran</SectionTitle>
        <dl className="vc-kv">
          <dt>Products answered</dt><dd>{t && t.productsReturned.length > 0 ? t.productsReturned.join(' · ') : '—'}</dd>
          <dt>Policy</dt><dd className="vc-mono">{r.policyName ?? '—'}</dd>
          <dt>LexisNexis status</dt><dd>{r.transactionStatus ?? '—'}{r.productStatus ? ` · product ${r.productStatus}` : ''}{r.reasonDescription ? ` · ${r.reasonDescription}` : ''}</dd>
          <dt>Sanctions / watchlist</dt>
          <dd>
            {t && t.watchlistFields.length > 0
              ? <>The response carried {t.watchlistFields.length} watchlist-related field(s): {t.watchlistFields.map((f) => `${f.key} = ${f.value}`).join(' · ')}. The app does not grade on them; OFAC comes from the OFAC check.</>
              : 'None in this response. This check does not search a watchlist; OFAC screening comes only from the OFAC check.'}
          </dd>
          <dt>Recorded as</dt><dd className="vc-mono">inquiry {r.inquiryId} · extract {r.extractId}</dd>
        </dl>
      </div>

      {t && (
        <div>
          <SectionTitle>Settings used for this run</SectionTitle>
          <SettingsTable rows={t.settings} />
        </div>
      )}

      <RawResponse text={r.rawResponse} label="Show raw response from LexisNexis (SSN-shaped numbers masked)" />
    </Panel>
  );
}
